package com.eladfinish.bible.contest.screens.completeVerse.ui

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.screens.completeVerse.CompleteVerseState
import com.eladfinish.bible.contest.screens.completeVerse.model.VerseWithBlanks
import com.eladfinish.bible.contest.ui.animations.rememberPopInModifier
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import com.eladfinish.bible.contest.ui.layout.isShortWideWindow
import com.eladfinish.bible.contest.ui.layout.readableWidth

@Composable
internal fun CompleteVerseContent(
    state: CompleteVerseState,
    textFieldValue: TextFieldValue,
    inputColor: Color,
    focusRequester: FocusRequester,
    onTextChanged: (TextFieldValue) -> Unit,
    onCheckCorrectness: () -> Unit,
    onImeNext: () -> Unit,
    onClear: () -> Unit,
    onRevealPress: () -> Unit,
    onRevealRelease: () -> Unit,
    onNextVerse: () -> Unit,
    onSourceClick: () -> Unit,
    onMoveLeft: () -> Unit,
    onMoveRight: () -> Unit,
    onMoveLeftLongPress: () -> Unit,
    onMoveRightLongPress: () -> Unit,
) {
    val input: @Composable () -> Unit = {
        VerseInputField(
            value = textFieldValue,
            onValueChange = { newValue ->
                onTextChanged(newValue)
                onCheckCorrectness()
            },
            onImeNext = onImeNext,
            onClear = onClear,
            hint = state.inputHint,
            enabled = state.isEditTextEnabled,
            textColor = inputColor,
            focusRequester = focusRequester,
            showRevealButton = state.isRevealButtonVisible,
            onRevealPress = onRevealPress,
            onRevealRelease = onRevealRelease,
        )
    }
    val verse: @Composable () -> Unit = {
        // key() resets animation state on each new verse.
        val verseId = state.verseWithBlanks.searchResult.id
        key(verseId) {
            AnimatedParchment(
                verseWithBlanks = state.verseWithBlanks,
                shouldAnimate = verseId.isNotEmpty(),
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
    val source: @Composable () -> Unit = {
        AnimatedVisibility(
            visible = state.isVerseSourceVisible,
            enter = fadeIn(),
            exit = fadeOut(),
        ) {
            VerseSourceLink(
                source = state.verseWithBlanks.searchResult.source,
                onClick = onSourceClick,
            )
        }
    }
    val buttons: @Composable () -> Unit = {
        when {
            state.showButtonNextVerse -> NextVerseButton(
                onClick = onNextVerse,
                modifier = Modifier.fillMaxWidth(),
            )

            state.showArrows -> ArrowButtonsRow(
                onMoveLeft = onMoveLeft,
                onMoveRight = onMoveRight,
                onMoveLeftLongPress = onMoveLeftLongPress,
                onMoveRightLongPress = onMoveRightLongPress,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        if (isShortWideWindow()) {
            // The input beside the verse, so that the keyboard leaves both of them on screen.
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .navigationBarInsetPadding()
                    .padding(horizontal = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                        .verticalScroll(rememberScrollState()),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Spacer(Modifier.height(4.dp))
                    input()
                    Spacer(Modifier.height(8.dp))
                    buttons()
                    source()
                    Spacer(Modifier.height(12.dp))
                }
                Column(
                    modifier = Modifier
                        .weight(1.4f)
                        .fillMaxHeight()
                        .verticalScroll(rememberScrollState()),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Spacer(Modifier.height(4.dp))
                    verse()
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .readableWidth()
                    .navigationBarInsetPadding()
                    .padding(horizontal = 8.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Spacer(Modifier.height(4.dp))
                input()
                Spacer(Modifier.height(8.dp))
                verse()
                source()
                Spacer(Modifier.weight(1f))
                buttons()
                Spacer(Modifier.height(12.dp))
            }
        }
    }
}

@Composable
private fun AnimatedParchment(
    verseWithBlanks: VerseWithBlanks,
    shouldAnimate: Boolean,
    modifier: Modifier = Modifier,
) {
    CompleteVerseParchmentView(
        verseWithBlanks = verseWithBlanks,
        modifier = modifier.then(rememberPopInModifier(key = Unit, enabled = shouldAnimate)),
    )
}
